// Acervo de documentos da empresa — a MESMA tabela servindo dois donos:
//
//   modo="gestao"  → módulo Administrativo: cadastra, edita, troca arquivo.
//   modo="leitura" → aba do Comercial: consulta e baixa, e mais nada.
//
// Era comercial-licitacoes/AbaDocumentos.tsx até 2026-08-19. O componente é um
// só de propósito: duas cópias divergem no primeiro ajuste de coluna.
// Quem pode o quê é decidido no backend (handlers/documentos.js); o `modo` aqui
// só evita oferecer botão que responderia 403.
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button, Input, Select, Tag, Table, Typography, Flex, Empty, Tooltip, Space, Grid,
  Checkbox, App as AntApp,
} from 'antd';
import {
  SearchOutlined, PlusOutlined, FileTextOutlined, UploadOutlined, DownloadOutlined,
  LinkOutlined, ColumnWidthOutlined, ContainerOutlined, InboxOutlined,
} from '@ant-design/icons';
import { useColunasRedimensionaveis } from '../../utils/colunasRedimensionaveis';
import type { ColumnsType } from 'antd/es/table';
import { fmtDataBr } from '../../utils/dataFmt';
import { Documento, CategoriaDoc, SubcategoriaDoc } from './types';
import { ModalDocumento } from './ModalDocumento';
import {
  listarDocumentos, listarCategorias, listarSubcategorias, obterDocumento, arquivarDocumento,
} from './api';

const { Text } = Typography;

const STATUS_CFG: Record<string, { label: string; color: string }> = {
  vigente:  { label: 'Vigente',  color: 'green' },
  vencendo: { label: 'Vencendo', color: 'orange' },
  vencido:  { label: 'Vencido',  color: 'red' },
};

export interface PainelDocumentosProps {
  /** 'gestao' = administrativo (cadastra/edita) · 'leitura' = comercial (consulta/baixa) */
  modo?: 'gestao' | 'leitura';
  /** Filtro vindo de fora (o checklist manda "olhe na categoria X"). Reaplica
   *  a cada mudança de objeto — é o que faz clicar duas vezes na mesma pista
   *  voltar a filtrar depois de o usuário ter limpado o campo à mão. */
  filtroInicial?: { categoria_codigo: string } | null;
  /** Editar aqui muda o CHECKLIST (classificar um documento fecha um item).
   *  A outra aba fica montada e velha se ninguém avisar — foi assim que
   *  classificar o Cartão CNPJ pareceu não ter efeito (20/08/2026). */
  onAcervoMudou?: () => void;
}

export function PainelDocumentosEmpresa({ modo = 'gestao', filtroInicial, onAcervoMudou }: PainelDocumentosProps) {
  const ehLeitura = modo === 'leitura';
  const { message, modal } = AntApp.useApp();
  // Larguras ajustáveis e persistidas no navegador — mesmo helper de Contratos
  // (utils/colunasRedimensionaveis.tsx), com as armadilhas já documentadas lá.
  const { aplicar: aplicarLarguras, componentes, restaurar: restaurarLarguras, ajustado } =
    useColunasRedimensionaveis<Documento>('adm:documentos:larguras');
  const bp = Grid.useBreakpoint();
  const [docs, setDocs] = useState<Documento[]>([]);
  const [categorias, setCategorias] = useState<CategoriaDoc[]>([]);
  const [subcategorias, setSubcategorias] = useState<SubcategoriaDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    categoria_codigo: '', subcategoria_codigo: '', status: '', q: '',
    incluir_arquivados: false,
  });
  const [editando, setEditando] = useState<Documento | null>(null);
  const [novo, setNovo] = useState(false);
  // L10 (2026-06-02) — quando true, abre o ModalDocumento ja com o modal de
  // troca de arquivo aberto (atalho da coluna Acoes).
  const [iniciarTroca, setIniciarTroca] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Categoria restrita (hoje só Sócios) ganha cadeado na lista e no filtro:
  // quem enxerga precisa saber que aquilo não é visível para o resto da empresa
  // antes de arrastar um documento para lá.
  const restritas = useMemo(
    () => new Set(categorias.filter((c) => c.restrita).map((c) => c.codigo)),
    [categorias]
  );

  const carregarCategorias = useCallback(async () => {
    try {
      const [cat, sub] = await Promise.all([listarCategorias(), listarSubcategorias()]);
      setCategorias(cat.categorias || []);
      setSubcategorias(sub.subcategorias || []);
    } catch { setCategorias([]); setSubcategorias([]); }
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listarDocumentos(filtros);
      setDocs(d.documentos || []);
      setErro(null);
    } catch (e: any) {
      setDocs([]);
      // 403 aqui é informação, não falha silenciosa: sem permissão a tabela
      // vazia pareceria "não há documentos".
      setErro(e?.response?.status === 403
        ? 'Você não tem permissão para ver o acervo de documentos da empresa.'
        : null);
    }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { carregarCategorias(); }, [carregarCategorias]);
  useEffect(() => { carregar(); }, [carregar]);
  // O checklist manda "procure na categoria X". Aplica por cima do que estiver
  // filtrado e limpa a subcategoria — o alvo aqui é justamente o documento que
  // ainda não tem subcategoria nenhuma.
  useEffect(() => {
    if (!filtroInicial?.categoria_codigo) return;
    setFiltros((p) => ({ ...p, categoria_codigo: filtroInicial.categoria_codigo, subcategoria_codigo: '' }));
  }, [filtroInicial]);

  // Arquivar/desarquivar. Pergunta antes de arquivar (some da lista padrão, e
  // a pessoa pode achar que sumiu de vez); desarquivar é reversível e direto.
  const arquivar = (row: Documento) => {
    const acao = async (motivo?: string) => {
      try {
        await arquivarDocumento({ id: row.id, arquivado: !row.arquivado, motivo });
        message.success(row.arquivado ? 'Documento desarquivado.' : 'Documento arquivado.');
        carregar();
        onAcervoMudou?.();
      } catch (e: any) {
        message.error('Não foi possível: ' + (e?.response?.data?.error || e.message));
      }
    };
    if (row.arquivado) { acao(); return; }
    modal.confirm({
      title: `Arquivar "${row.titulo}"?`,
      content: 'Ele sai do checklist, do aviso semanal e da lista padrão — mas continua no sistema, '
        + 'e volta pelo filtro "incluir arquivados". Use para documento substituído por uma versão nova.',
      okText: 'Arquivar', cancelText: 'Cancelar',
      onOk: () => acao(),
    });
  };

  // Baixa o arquivo: pede o presigned URL ao backend (action 'get' com_download) e abre.
  const baixar = async (row: Documento) => {
    const hide = message.loading('Gerando link…', 0);
    try {
      const d = await obterDocumento(row.id, true);
      hide();
      if (d.download_url) window.open(d.download_url, '_blank', 'noopener,noreferrer');
      else message.error('Este documento não tem arquivo pra baixar.');
    } catch (e: any) {
      hide();
      message.error('Erro ao baixar: ' + (e.response?.data?.error || e.message));
    }
  };

  // Piso da tabela = colunas fixas visíveis + espaço decente para o Título.
  // Recalcula quando uma coluna some por breakpoint; se ficasse constante, o
  // celular rolaria na horizontal por causa de colunas que nem aparecem.
  // ⚠ A conta é sobre a largura DA TABELA, não da janela: a barra lateral do
  //   AppShell tem 264px fixos (App.tsx) e a página tem 16 de padding de cada
  //   lado. Numa janela de 1536 sobram ~1240 aqui dentro — foi por ignorar isso
  //   que a primeira tentativa deixou o Título com 200px e ele continuou
  //   quebrando.
  const larguraMinima = useMemo(() => {
    const TITULO_MINIMO = 260;
    let soma = 138 /* Código */ + 84 /* Status */ + 170 /* Categoria */
      + 92 /* Validade */ + (ehLeitura ? 64 : 132) /* Ações */;
    if (bp.lg) soma += 130;       // Emitente
    if (bp.xl) soma += 88 + 120;  // Emissão + Número
    return soma + TITULO_MINIMO;  // gestão, tudo visível: 954 + 260 = 1214
  }, [bp.lg, bp.xl, ehLeitura]);

  // ── Larguras ───────────────────────────────────────────────────────────
  // A tabela declarava width e não usava `tableLayout="fixed"`: no antd (e no
  // HTML) largura sem layout fixo é SUGESTÃO. O navegador redistribuía pelo
  // conteúdo, e quem mandava era a célula com o texto mais longo sem espaço —
  // um número tipo "607.057.2.829.388.072/0001-33" esticava a coluna Número
  // (quase toda vazia, "—") e espremia Código, Título e Validade a ponto de
  // "17/12/2025" quebrar em duas linhas. Daí as linhas altíssimas do print.
  //
  // Com layout fixo a largura vale ao pé da letra, e quem fica elástico é o
  // Título — que é o que a pessoa lê.
  const cols: ColumnsType<Documento> = [
    {
      title: 'Código', dataIndex: 'codigo', key: 'cod', width: 138,
      // Código é técnico e de tamanho conhecido (HYD-VEI-APOL-2024-001): cabe
      // numa linha, e quebrado em três vira ruído.
      render: (v: string | null) => v
        ? <Text code style={{ fontSize: 10, whiteSpace: 'nowrap' }}>{v}</Text>
        : <Text type="secondary" style={{ fontSize: 10 }}>—</Text>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'st', width: 84,
      render: (v: string, row) => {
        // Arquivado ganha o lugar do status: "Vencido" num papel substituído é
        // alarme falso, e é exatamente o que confundia na lista.
        if (row.arquivado) {
          return (
            <Tooltip title={row.substituido_por_codigo
              ? `Substituído por ${row.substituido_por_codigo}`
              : 'Arquivado — não conta no checklist nem no aviso'}>
              <Tag>Arquivado</Tag>
            </Tooltip>
          );
        }
        const c = STATUS_CFG[v] || { label: v, color: 'default' };
        return <Tag color={c.color}>{c.label}</Tag>;
      },
    },
    {
      title: 'Categoria', dataIndex: 'categoria_nome', key: 'cat', width: 170,
      render: (v, row) => (
        <div>
          {/* Só aparece quando o documento DISCORDA da categoria. Marcar todo
              mundo com "visível/invisível" seria ruído: 100% herda hoje, e o
              que interessa saber é onde alguém decidiu diferente à mão. */}
          {typeof row.visivel_comercial_override === 'boolean' && (
            <Tooltip title={row.visivel_comercial_override
              ? 'Este documento aparece para o Comercial, mesmo a categoria estando desligada'
              : 'Este documento NÃO aparece para o Comercial, mesmo a categoria estando ligada'}>
              <Tag color={row.visivel_comercial_override ? 'green' : 'default'}
                style={{ fontSize: 10, marginBottom: 2 }}>
                {row.visivel_comercial_override ? 'no Comercial' : 'fora do Comercial'}
              </Tag>
            </Tooltip>
          )}
          {/* A Tag também tem que caber: "Trabalhista/Previdenciária" estourava
              a coluna e quebrava em duas ou três linhas. */}
          <Tooltip title={v}>
            <Tag color={restritas.has(row.categoria_codigo) ? 'red' : 'blue'}
              style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {restritas.has(row.categoria_codigo) ? `🔒 ${v}` : v}
            </Tag>
          </Tooltip>
          {row.subcategoria_nome && (
            <div style={{ fontSize: 10, color: '#666', marginTop: 2, overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={row.subcategoria_nome}>{row.subcategoria_nome}</div>
          )}
        </div>
      ),
    },
    {
      // SEM width: é a coluna elástica, fica com toda a sobra da tela. Em
      // monitor grande ela cresce em vez de deixar espaço morto na Número.
      title: 'Título', dataIndex: 'titulo', key: 'tit',
      render: (v, row) => (
        <div>
          {/* Flex: o título ocupa a sobra e os selos (sumário da IA, contagem
              de vínculos) ficam na MESMA linha, encostados à direita dele — se
              fossem irmãos soltos, o clamp de 2 linhas os jogaria para baixo e
              a linha cresceria de novo. */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            {/* Teto de 2 linhas: mesmo espremido, a linha não vira um parágrafo
                de cinco linhas como no print. O texto inteiro fica no hover e
                no modal. */}
            <Text strong title={v} style={{
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', flex: 1, minWidth: 0,
            }}>{v}</Text>
            {row.tem_sumario && (
              <Tooltip title="Sumário gerado pela IA disponível">
                <FileTextOutlined style={{ color: '#0a7f4a', fontSize: 11, marginTop: 4 }} />
              </Tooltip>
            )}
            {!!row.vinculos_count && row.vinculos_count > 0 && (
              <Tooltip title={`${row.vinculos_count} documento(s) vinculado(s)`}>
                <Tag icon={<LinkOutlined />} color="purple"
                  style={{ fontSize: 9, padding: '0 4px', marginInlineEnd: 0 }}>
                  {row.vinculos_count}
                </Tag>
              </Tooltip>
            )}
          </div>
          {row.descricao && (
            <div title={row.descricao} style={{
              fontSize: 10, color: '#888', marginTop: 2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{row.descricao}</div>
          )}
        </div>
      ),
    },
    // Emitente e Número são consulta, não leitura: cortam com reticências e o
    // texto inteiro fica no title (hover). Some nas telas menores — em vez de
    // roubar espaço do Título, que é o que identifica o documento.
    {
      title: 'Emitente', dataIndex: 'emitente', key: 'em', width: 130,
      ellipsis: { showTitle: true }, responsive: ['lg'],
      render: (v) => v || '—',
    },
    {
      // xl (≥1200), não xxl (≥1600): a janela de trabalho do dia a dia tem
      // ~1536 e esconder o número ali tiraria um campo que a pessoa usa.
      title: 'Número', dataIndex: 'numero_documento', key: 'nd', width: 120,
      ellipsis: { showTitle: true }, responsive: ['xl'],
      render: (v) => v || '—',
    },
    {
      title: 'Emissão', dataIndex: 'data_emissao', key: 'de', width: 88,
      responsive: ['xl'],
      render: (v) => <span style={{ whiteSpace: 'nowrap' }}>{fmtDataBr(v) || '—'}</span>,
    },
    {
      // Data NUNCA pode quebrar: "17/12/202 / 6" foi o pior sintoma do print.
      title: 'Validade', dataIndex: 'data_validade', key: 'dv', width: 92,
      render: (v, row) => {
        if (!v) return <Text type="secondary" style={{ fontSize: 11 }}>Permanente</Text>;
        const cor = row.status === 'vencido' ? '#B91626'
          : row.status === 'vencendo' ? '#B07500' : '#444';
        return <Text style={{ color: cor, fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDataBr(v)}</Text>;
      },
    },
    // L10 (2026-06-02) — botao direto pra trocar o arquivo sem precisar abrir
    // o modal e procurar o botao no topo.
    {
      title: 'Ações', key: 'acoes', width: ehLeitura ? 64 : 132, align: 'center', fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Tooltip title="Baixar o arquivo">
            <Button size="small" icon={<DownloadOutlined />}
              onClick={(e) => { e.stopPropagation(); baixar(row); }} />
          </Tooltip>
          {/* Trocar arquivo é do administrativo — em leitura nem aparece */}
          {!ehLeitura && (
            <Tooltip title="Substituir o arquivo (CND renovada, atestado revisado, etc) mantendo código e vínculos">
              <Button size="small" icon={<UploadOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditando(row);
                  setIniciarTroca(true);
                }}>
                Trocar
              </Button>
            </Tooltip>
          )}
          {/* Arquivar é para papel SUBSTITUÍDO (a apólice de 2025 quando a de
              2026 já está no acervo): some do que cobra, continua consultável.
              Documento errado se corrige ou se exclui — não é caso disto. */}
          {!ehLeitura && (
            <Tooltip title={row.arquivado
              ? 'Desarquivar: volta a contar no checklist e nos avisos'
              : 'Arquivar: tira do checklist e do aviso semanal sem apagar nada'}>
              <Button size="small" type="text"
                icon={row.arquivado ? <InboxOutlined /> : <ContainerOutlined />}
                onClick={(e) => { e.stopPropagation(); arquivar(row); }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Flex gap={8} align="center" wrap="wrap" style={{ marginBottom: 14 }}>
        <Input value={filtros.q} onChange={e => setFiltros({ ...filtros, q: e.target.value })}
          placeholder="Buscar..." prefix={<SearchOutlined style={{ color: '#aaa' }} />}
          size="small" style={{ flex: 1, minWidth: 220 }} allowClear />
        <Select value={filtros.categoria_codigo || undefined}
          onChange={(v) => setFiltros({ ...filtros, categoria_codigo: v || '', subcategoria_codigo: '' })}
          placeholder="Todas categorias" allowClear size="small" style={{ width: 200 }}
          options={categorias.map(c => ({ label: c.restrita ? `🔒 ${c.nome}` : c.nome, value: c.codigo }))} />
        <Select value={filtros.subcategoria_codigo || undefined}
          onChange={(v) => setFiltros({ ...filtros, subcategoria_codigo: v || '' })}
          placeholder={filtros.categoria_codigo ? 'Todas subcategorias' : 'Selecione categoria primeiro'}
          allowClear size="small" style={{ width: 200 }} disabled={!filtros.categoria_codigo}
          options={subcategorias
            .filter(s => !filtros.categoria_codigo || s.categoria_codigo === filtros.categoria_codigo)
            .map(s => ({ label: s.nome, value: s.codigo }))} />
        <Select value={filtros.status || undefined}
          onChange={(v) => setFiltros({ ...filtros, status: v || '' })}
          placeholder="Todos status" allowClear size="small" style={{ width: 150 }}
          options={[
            { label: 'Vigente', value: 'vigente' },
            { label: 'Vencendo (30d)', value: 'vencendo' },
            { label: 'Vencido', value: 'vencido' },
          ]} />
        <Tooltip title="Documento arquivado é papel substituído: sai do checklist e do aviso, mas continua aqui">
          <Checkbox checked={filtros.incluir_arquivados}
            onChange={(e) => setFiltros({ ...filtros, incluir_arquivados: e.target.checked })}
            style={{ fontSize: 12 }}>
            Arquivados
          </Checkbox>
        </Tooltip>
        {ajustado && (
          <Tooltip title="Voltar as colunas para a largura padrão">
            <Button size="small" icon={<ColumnWidthOutlined />} onClick={restaurarLarguras} />
          </Tooltip>
        )}
        {!ehLeitura && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setNovo(true)}>Novo documento</Button>
        )}
      </Flex>

      <Table
        rowKey="id" columns={aplicarLarguras(cols)} components={componentes}
        dataSource={docs} loading={loading}
        // Sem `fixed` a largura declarada é só sugestão e o arrasto não parece
        // fazer efeito (a lição está em OperacionalContratos.tsx).
        tableLayout="fixed"
        // ⚠ x NUMÉRICO: o antd trata como MIN-width e a tabela estica para
        //   ocupar a tela quando sobra espaço. Com 'max-content' ela trava na
        //   soma das colunas e cria rolagem horizontal SEMPRE (incidente de
        //   13/08/2026 em Contratos). O piso acompanha as colunas que somem
        //   por breakpoint — senão a rolagem apareceria no celular por causa
        //   de colunas que nem estão na tela.
        scroll={{ x: larguraMinima }}
        size="small" pagination={{ pageSize: 50 }}
        onRow={(record) => ({ onClick: () => setEditando(record), style: { cursor: 'pointer' } })}
        locale={{ emptyText: <Empty description={erro
          || (ehLeitura
            ? 'Nenhum documento disponível para consulta.'
            : 'Nenhum documento cadastrado. Suba CATs, certidões, balanços e atestados — a IA vai categorizar e extrair as datas.')} /> }}
      />

      {(editando || novo) && (
        <ModalDocumento
          documento={editando}
          somenteLeitura={ehLeitura}
          categorias={categorias}
          subcategorias={subcategorias}
          todosDocs={docs}
          iniciarTroca={iniciarTroca}
          onClose={() => { setEditando(null); setNovo(false); setIniciarTroca(false); }}
          onSaved={() => { setEditando(null); setNovo(false); setIniciarTroca(false); carregar(); onAcervoMudou?.(); }}
        />
      )}
    </>
  );
}
